import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * A full-bleed tinted band: an unconstrained outer surface wrapping a centred
 * inner column.
 *
 * The `tone` class is what makes the band theme-correct — it re-declares the
 * semantic tokens for its subtree (see `LandingStyles`), so children inside an
 * oxblood band write `text-muted-foreground` and get the on-oxblood muted
 * rather than the page's. Nothing inside a band should name a brand colour
 * directly.
 */
const toneClass = {
    ink: 'mn-on-ink',
    paper: 'bg-card',
    oxblood: 'mn-on-oxblood',
} as const;

export function Band({
    id,
    tone,
    children,
    className,
    ...props
}: ComponentProps<'div'> & {
    tone: keyof typeof toneClass;
    children: ReactNode;
}) {
    return (
        <div
            {...props}
            id={id}
            className={cn(
                // scroll-mt clears the sticky header, which would otherwise
                // cover the top of whatever an anchor link jumps to.
                'relative z-[1] w-full scroll-mt-[72px] bg-background text-foreground',
                toneClass[tone],
                className,
            )}
        >
            <section className="mx-auto w-full max-w-[1400px] px-[clamp(20px,5vw,72px)] py-[clamp(56px,9vw,88px)]">
                {children}
            </section>
        </div>
    );
}
